import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2,
  Plus,
  CheckCircle2,
  Circle,
  Clock,
  Ban,
  Sparkles,
  ArrowLeft,
  Calendar,
  ChevronDown,
  ChevronRight,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { getProjectWithDetails } from '@/lib/database';
import {
  getProjectTasks,
  createTask,
  createTasks,
  updateTask,
  deleteTask,
  type TaskRow,
  type TaskStatus,
  type TaskPriority,
} from '@/lib/db/tasks';
import { generateTasksFromDocuments } from '@/lib/llm';
import { useWorkspace } from '@/store/workspace';
import { fetchDocumentTextsForProject } from '@/lib/projectRegenerate';
import { getProjectDocuments } from '@/lib/db/documents';

const statusConfig: Record<TaskStatus, { icon: typeof Circle; label: string; color: string }> = {
  todo: { icon: Circle, label: 'To Do', color: 'text-gray-400' },
  in_progress: { icon: Clock, label: 'In Progress', color: 'text-blue-500' },
  complete: { icon: CheckCircle2, label: 'Complete', color: 'text-green-500' },
  cancelled: { icon: Ban, label: 'Cancelled', color: 'text-gray-300' },
};

const priorityConfig: Record<TaskPriority, { label: string; color: string; bg: string }> = {
  low: { label: 'Low', color: 'text-gray-500', bg: 'bg-gray-100' },
  medium: { label: 'Medium', color: 'text-yellow-700', bg: 'bg-yellow-50' },
  high: { label: 'High', color: 'text-orange-700', bg: 'bg-orange-50' },
  urgent: { label: 'Urgent', color: 'text-red-700', bg: 'bg-red-50' },
};

const statusOrder: TaskStatus[] = ['todo', 'in_progress', 'complete', 'cancelled'];

// Local YYYY-MM-DD. en-CA formats dates as ISO (YYYY-MM-DD) in local time,
// matching how due dates are stored and how the date input expects them.
const todayDate = (): string => new Date().toLocaleDateString('en-CA');

// Order the task list by due date, soonest first. Tasks without a due date
// sink to the bottom; created_at breaks ties for a stable order.
const byDueDateThenCreated = (a: TaskRow, b: TaskRow): number => {
  if (a.due_date && b.due_date) {
    if (a.due_date !== b.due_date) return a.due_date < b.due_date ? -1 : 1;
  } else if (a.due_date) {
    return -1;
  } else if (b.due_date) {
    return 1;
  }
  return a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0;
};

export function ProjectTasks({
  embedded = false,
  onTasksChanged,
}: {
  embedded?: boolean;
  /** Notified with the current task list after load and every mutation, so a
   *  host (the Details tab) can derive overall completion from tasks without
   *  maintaining a second copy of the data. */
  onTasksChanged?: (tasks: TaskRow[]) => void;
} = {}) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canEdit } = useWorkspace();
  const isReadOnly = !canEdit();

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');

  // Generate tasks state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [documentCount, setDocumentCount] = useState(0);
  const [genStartDate, setGenStartDate] = useState('');
  const [genEndDate, setGenEndDate] = useState('');
  const [generatedTasks, setGeneratedTasks] = useState<Array<{
    title: string;
    description: string | null;
    priority: TaskPriority;
    dueDate: string | null;
    assignedTo: string | null;
    selected: boolean;
  }>>([]);
  const [showReview, setShowReview] = useState(false);
  const [saving, setSaving] = useState(false);

  // Add task inline
  // Id of the just-added task, so its row opens expanded for editing the rest
  // of its fields (due date, assignee, etc.) with the same inline editor.
  const [newTaskId, setNewTaskId] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!id) return;

      // Document count drives the "no documents yet" message. Fetch it
      // independently so a failure in the task/project calls below can't
      // silently mask the real document result.
      try {
        const docs = await getProjectDocuments(id);
        setDocumentCount(docs.length);
      } catch (error) {
        console.error('Failed to load document count:', error);
      }

      try {
        const [project, taskData] = await Promise.all([
          getProjectWithDetails(id),
          getProjectTasks(id),
        ]);
        if (project) {
          setProjectName(project.name);
          setProjectDescription(project.description || '');
          setGenStartDate(project.start_date || '');
          setGenEndDate(project.end_date || '');
        }
        setTasks(taskData);
      } catch (error) {
        console.error('Failed to load tasks:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // Report the current task list up to a host (the Details tab) after load and
  // every mutation so it can derive overall completion from tasks. Runs on
  // initial mount too, giving the host the loaded set without a second fetch.
  useEffect(() => {
    onTasksChanged?.(tasks);
  }, [tasks, onTasksChanged]);

  const handleAddTask = async () => {
    if (!id) return;
    try {
      // Create immediately (status defaults to "To Do", due date to today) and
      // pin the new row at the top, expanded, so the user fills in everything
      // — name, owner, due date — in the same inline editor with no separate
      // title-first step. It re-sorts into due-date order once collapsed
      // (newTaskId is cleared on collapse).
      const task = await createTask(id, { title: 'New task', dueDate: todayDate() });
      setTasks([task, ...tasks]);
      setNewTaskId(task.id);
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    // Deleting is destructive and permanent — distinct from completing a task,
    // which just sets its status. Confirm first.
    if (!window.confirm('Delete this task? This cannot be undone.')) return;
    try {
      await deleteTask(taskId);
      setTasks(tasks.filter(t => t.id !== taskId));
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleUpdateTask = async (taskId: string, updates: Partial<{ title: string; description: string | null; status: TaskStatus; priority: TaskPriority; dueDate: string | null; assignedTo: string | null }>) => {
    try {
      const updated = await updateTask(taskId, updates);
      setTasks(tasks.map(t => t.id === taskId ? updated : t));
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  // --- Generate tasks flow ---
  const handleGenerate = async () => {
    if (!id) return;
    setGenerating(true);
    try {
      // Reuse the documents already attached to the project rather than
      // asking the user to re-upload. Same extractor the import/regenerate
      // flow uses.
      const texts = await fetchDocumentTextsForProject(id);
      if (texts.length === 0) {
        alert('This project has no documents yet. Add documents on the Documents tab, then generate tasks.');
        return;
      }
      const projectContext = projectName
        ? `${projectName}${projectDescription ? ': ' + projectDescription : ''}`
        : undefined;

      const result = await generateTasksFromDocuments(
        texts,
        genStartDate || null,
        genEndDate || null,
        projectContext
      );

      setGeneratedTasks(result.map(t => ({ ...t, selected: true })));
      setShowReview(true);
    } catch (error) {
      console.error('Failed to generate tasks:', error);
      alert(error instanceof Error ? error.message : 'Failed to generate tasks');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveGeneratedTasks = async () => {
    if (!id) return;
    const selected = generatedTasks.filter(t => t.selected);
    if (selected.length === 0) return;

    setSaving(true);
    try {
      const created = await createTasks(id, selected.map((t, i) => ({
        title: t.title,
        description: t.description,
        priority: t.priority,
        dueDate: t.dueDate,
        assignedTo: t.assignedTo,
        sortOrder: tasks.length + i,
      })));
      setTasks([...tasks, ...created]);
      setShowGenerateModal(false);
      setShowReview(false);
      setGeneratedTasks([]);
    } catch (error) {
      console.error('Failed to save tasks:', error);
    } finally {
      setSaving(false);
    }
  };

  // Open the generate modal in its first (confirm) stage, clearing any leftover
  // review state from a previous run.
  const openGenerateModal = () => {
    setShowReview(false);
    setGeneratedTasks([]);
    setShowGenerateModal(true);
  };

  // Close the generate modal. If the user has generated tasks they haven't
  // saved yet, warn them first — closing discards everything.
  const closeGenerateModal = () => {
    if (showReview && generatedTasks.length > 0) {
      const ok = window.confirm(
        'These generated tasks have not been saved. Close and discard them?',
      );
      if (!ok) return;
    }
    setShowGenerateModal(false);
    setShowReview(false);
    setGeneratedTasks([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const todoTasks = tasks.filter(t => t.status === 'todo');
  const inProgressTasks = tasks.filter(t => t.status === 'in_progress');
  const completeTasks = tasks.filter(t => t.status === 'complete');
  const completionRate = tasks.length > 0
    ? Math.round((completeTasks.length / tasks.filter(t => t.status !== 'cancelled').length) * 100) || 0
    : 0;

  // Display order: due date soonest-first. A just-added task is pinned to the
  // top while it's still being edited (newTaskId set); once it collapses out
  // of edit mode newTaskId is cleared and it drops into its sorted position.
  const sortedTasks = [...tasks].sort(byDueDateThenCreated);
  const displayTasks = newTaskId
    ? [
        ...sortedTasks.filter(t => t.id === newTaskId),
        ...sortedTasks.filter(t => t.id !== newTaskId),
      ]
    : sortedTasks;

  return (
    <div className={embedded ? 'space-y-6' : 'max-w-4xl mx-auto space-y-6'}>
      {/* Header */}
      <div className="flex items-center justify-between">
        {embedded ? (
          <h2 className="text-lg font-semibold text-gray-900">Tasks</h2>
        ) : (
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/projects/${id}`)}
              className="text-gray-400 hover:text-gray-600"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
              <p className="text-sm text-gray-500">{projectName}</p>
            </div>
          </div>
        )}
        <div className="flex items-center gap-2">
          {!isReadOnly && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={openGenerateModal}
              >
                <Sparkles className="h-4 w-4 mr-1" />
                Generate Tasks
              </Button>
              <Button
                size="sm"
                onClick={handleAddTask}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Task
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      {tasks.length > 0 && (
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-gray-700">{todoTasks.length}</div>
            <div className="text-xs text-gray-500">To Do</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{inProgressTasks.length}</div>
            <div className="text-xs text-gray-500">In Progress</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{completeTasks.length}</div>
            <div className="text-xs text-gray-500">Complete</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-ingesta-blue-600">{completionRate}%</div>
            <div className="text-xs text-gray-500">Done</div>
          </div>
        </div>
      )}

      {/* Generate Tasks modal: confirm → review → save, all in one dialog.
          Closing mid-review warns that nothing has been saved. */}
      <Dialog
        open={showGenerateModal}
        onOpenChange={(open) => {
          if (!open) closeGenerateModal();
        }}
      >
        <DialogContent className="max-w-2xl">
          {!showReview ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-ingesta-blue-500" />
                  Generate Tasks from Documents
                </DialogTitle>
                <DialogDescription>
                  {documentCount === 0
                    ? 'This project has no documents yet. Add documents on the Documents tab, then come back to generate a task list.'
                    : `We'll analyze the ${documentCount} document${documentCount === 1 ? '' : 's'} already attached to this project and generate an actionable task list with due dates.`}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={closeGenerateModal}>
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerate}
                  disabled={documentCount === 0 || generating}
                >
                  {generating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-1" />
                      Generate Tasks
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  Review Generated Tasks
                </DialogTitle>
                <DialogDescription>
                  {generatedTasks.filter(t => t.selected).length} of {generatedTasks.length}{' '}
                  selected. Deselect any you don't want — nothing is saved until you click Add.
                </DialogDescription>
              </DialogHeader>

              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setGeneratedTasks(generatedTasks.map(t => ({ ...t, selected: true })))}
                >
                  Select All
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setGeneratedTasks(generatedTasks.map(t => ({ ...t, selected: false })))}
                >
                  Deselect All
                </Button>
              </div>

              <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                {generatedTasks.map((task, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-lg border transition-colors",
                      task.selected ? "bg-white border-green-200" : "bg-gray-50 border-gray-200 opacity-60"
                    )}
                  >
                    <button
                      onClick={() => {
                        const updated = [...generatedTasks];
                        updated[idx] = { ...updated[idx], selected: !updated[idx].selected };
                        setGeneratedTasks(updated);
                      }}
                      className="mt-0.5"
                    >
                      {task.selected ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      ) : (
                        <Circle className="h-5 w-5 text-gray-300" />
                      )}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{task.title}</div>
                      {task.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{task.description}</p>
                      )}
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={cn("text-xs px-1.5 py-0.5 rounded", priorityConfig[task.priority].bg, priorityConfig[task.priority].color)}>
                          {priorityConfig[task.priority].label}
                        </span>
                        {task.dueDate && (
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(task.dueDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                        {task.assignedTo && (
                          <span className="text-xs text-gray-500">{task.assignedTo}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={closeGenerateModal}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveGeneratedTasks}
                  disabled={saving || generatedTasks.filter(t => t.selected).length === 0}
                >
                  {saving ? (
                    <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving...</>
                  ) : (
                    <>Add {generatedTasks.filter(t => t.selected).length} Tasks</>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Task List */}
      {tasks.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No tasks yet</h3>
            <p className="text-sm text-gray-500 mb-4">
              Add tasks manually or generate them from your project documents using AI.
            </p>
            {!isReadOnly && (
              <div className="flex justify-center gap-2">
                <Button variant="outline" onClick={handleAddTask}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Task
                </Button>
                <Button onClick={openGenerateModal}>
                  <Sparkles className="h-4 w-4 mr-1" />
                  Generate from Documents
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1">
          {displayTasks.map((task) => (
            <TaskItem
              key={task.id}
              task={task}
              defaultExpanded={task.id === newTaskId}
              onExpandedChange={
                task.id === newTaskId
                  ? (expanded) => { if (!expanded) setNewTaskId(null); }
                  : undefined
              }
              onUpdate={(updates) => handleUpdateTask(task.id, updates)}
              onDelete={() => handleDeleteTask(task.id)}
              readOnly={isReadOnly}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TaskItem({
  task,
  defaultExpanded = false,
  onExpandedChange,
  onUpdate,
  onDelete,
  readOnly,
}: {
  task: TaskRow;
  defaultExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  onUpdate: (updates: Partial<{ title: string; description: string | null; status: TaskStatus; priority: TaskPriority; dueDate: string | null; assignedTo: string | null }>) => void;
  onDelete: () => void;
  readOnly: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    onExpandedChange?.(next);
  };
  // Local draft state for the free-text fields, committed on blur. Status,
  // priority and due date commit immediately (they're discrete pickers).
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description ?? '');
  const [assignedTo, setAssignedTo] = useState(task.assigned_to ?? '');

  // Re-sync drafts if the task changes underneath us (e.g. after a save).
  useEffect(() => {
    setTitle(task.title);
    setDescription(task.description ?? '');
    setAssignedTo(task.assigned_to ?? '');
  }, [task.title, task.description, task.assigned_to]);

  const priority = priorityConfig[task.priority];
  const status = statusConfig[task.status];
  const isComplete = task.status === 'complete';

  const commitTitle = () => {
    const next = title.trim();
    if (next && next !== task.title) onUpdate({ title: next });
    else setTitle(task.title);
  };
  const commitDescription = () => {
    const next = description.trim();
    if (next !== (task.description ?? '')) onUpdate({ description: next || null });
  };
  const commitAssignee = () => {
    const next = assignedTo.trim();
    if (next !== (task.assigned_to ?? '')) onUpdate({ assignedTo: next || null });
  };

  // Dedicated complete toggle — distinct from deleting. Marking incomplete
  // returns the task to "To Do".
  const toggleComplete = () => onUpdate({ status: isComplete ? 'todo' : 'complete' });

  return (
    <div className={cn(
      "rounded-lg border transition-colors",
      expanded ? "border-gray-400 bg-gray-50/50" : "border-transparent hover:bg-gray-50",
      task.status === 'cancelled' && "opacity-50",
    )}>
      {/* Summary row */}
      <div className="flex items-center gap-3 px-3 py-2.5">
        <button
          onClick={toggleComplete}
          disabled={readOnly}
          className={cn(
            "flex-shrink-0",
            isComplete ? "text-green-500" : "text-gray-300",
            !readOnly && !isComplete && "hover:text-gray-400",
          )}
          title={isComplete ? "Mark as not complete" : "Mark complete"}
        >
          {isComplete ? <CheckCircle2 className="h-5 w-5" /> : <Circle className="h-5 w-5" />}
        </button>

        <button
          onClick={toggleExpanded}
          className="flex-1 min-w-0 text-left flex items-start gap-1.5"
          title={expanded ? "Collapse" : "Expand"}
        >
          {expanded
            ? <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
            : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />}
          <span className="min-w-0">
            <span className={cn("text-sm block truncate", isComplete && "line-through text-gray-500")}>
              {task.title}
            </span>
            {task.description && !expanded && (
              <span className="text-xs text-gray-400 truncate mt-0.5 block">{task.description}</span>
            )}
          </span>
        </button>

        <div className="flex items-center gap-2 flex-shrink-0">
          {task.status !== 'todo' && task.status !== 'complete' && (
            <span className={cn("text-xs hidden sm:inline", status.color)}>{status.label}</span>
          )}
          <span className={cn("text-xs px-1.5 py-0.5 rounded hidden sm:inline", priority.bg, priority.color)}>
            {priority.label}
          </span>
          {task.due_date && (
            <span className="text-xs text-gray-400 hidden sm:flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {new Date(task.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </span>
          )}
          {task.assigned_to && (
            <span className="text-xs text-gray-400 hidden md:inline">{task.assigned_to}</span>
          )}
        </div>
      </div>

      {/* Expanded editor */}
      {expanded && (
        <div className="px-3 pb-3 border-t border-gray-100">
          {readOnly ? (
            <div className="pt-3 space-y-1 text-sm">
              {task.description && <p className="text-gray-600">{task.description}</p>}
              <p className="text-xs text-gray-400">You have read-only access to this project.</p>
            </div>
          ) : (
            <div className="pt-3 space-y-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Title</label>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={commitTitle}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  className="h-8 text-sm"
                  // Newly added tasks open focused with the placeholder title
                  // selected, so the user can immediately type the real name.
                  autoFocus={defaultExpanded}
                  onFocus={defaultExpanded ? (e) => e.target.select() : undefined}
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Description</label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onBlur={commitDescription}
                  rows={2}
                  placeholder="Add a description…"
                  className="text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Status</label>
                  <Select
                    value={task.status}
                    onChange={(e) => onUpdate({ status: e.target.value as TaskStatus })}
                    containerClassName="w-full"
                    className="h-8 text-sm"
                  >
                    {statusOrder.map((s) => (
                      <option key={s} value={s}>{statusConfig[s].label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Priority</label>
                  <Select
                    value={task.priority}
                    onChange={(e) => onUpdate({ priority: e.target.value as TaskPriority })}
                    containerClassName="w-full"
                    className="h-8 text-sm"
                  >
                    {(['low', 'medium', 'high', 'urgent'] as TaskPriority[]).map((p) => (
                      <option key={p} value={p}>{priorityConfig[p].label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Assignee</label>
                  <Input
                    value={assignedTo}
                    onChange={(e) => setAssignedTo(e.target.value)}
                    onBlur={commitAssignee}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    placeholder="Unassigned"
                    className="h-8 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Due date</label>
                  <Input
                    type="date"
                    value={task.due_date ?? ''}
                    onChange={(e) => onUpdate({ dueDate: e.target.value || null })}
                    className="h-8 text-sm"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDelete}
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete task
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
